import { computeCoverCropRect } from '@/lib/cover/computeCoverCropRect';

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Could not decode captured frame'));
    img.src = src;
  });
}

/**
 * Crops a captured full-source frame (a data URL) to the export aspect around
 * the preview's reframe center and zoom, so a "use current frame" cover shows what the
 * preview shows instead of the worker's plain center-crop. Browser-only
 * (Image + canvas); the crop math lives in `computeCoverCropRect`. Returns the
 * input unchanged when no crop is needed.
 */
export async function cropFrameDataUrl(
  dataUrl: string,
  {
    targetAspect,
    centerX,
    centerY = null,
    zoom = 1,
    quality = 0.92,
  }: {
    targetAspect: number;
    centerX: number | null;
    centerY?: number | null;
    zoom?: number;
    quality?: number;
  },
): Promise<string> {
  const img = await loadImage(dataUrl);
  const sourceWidth = img.naturalWidth;
  const sourceHeight = img.naturalHeight;
  const rect = computeCoverCropRect({
    sourceWidth, sourceHeight, targetAspect, centerX, centerY, zoom,
  });
  if (rect.width === sourceWidth && rect.height === sourceHeight) return dataUrl;

  const canvas = document.createElement('canvas');
  canvas.width = rect.width;
  canvas.height = rect.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');
  ctx.drawImage(img, rect.x, rect.y, rect.width, rect.height, 0, 0, rect.width, rect.height);
  return canvas.toDataURL('image/jpeg', quality);
}
